import React, { createContext, useContext, useState, useCallback } from 'react';

const GlobalErrorContext = createContext();

// Centralized error routing configuration
const ERROR_ROUTING = {
  // User-facing errors (show localized message to user)
  'ERR_PUZZLE_NOT_SOLVED': { type: 'user', severity: 'non-critical' },
  'ERR_INVALID_MOVE': { type: 'user', severity: 'non-critical' },
  'ERR_PUZZLE_ALREADY_SOLVED': { type: 'user', severity: 'non-critical' },
  'ERR_NO_HINTS_AVAILABLE': { type: 'user', severity: 'non-critical' },
  'ERR_CANNOT_UNDO': { type: 'user', severity: 'non-critical' },
  'ERR_INVALID_CELL_VALUE': { type: 'user', severity: 'non-critical' },
  
  // System errors that should be hidden from user but logged
  'ERR_NOT_AUTHENTICATED': { type: 'system', severity: 'critical', action: 'refresh_token' },
  'ERR_AUTH_FAILED': { type: 'system', severity: 'critical', action: 'refresh_token' },
  'ERR_INVALID_METADATA_VERSION': { type: 'system', severity: 'non-critical', action: 'refresh_metadata' },
  'ERR_PERMISSION_DENIED': { type: 'system', severity: 'non-critical', action: 'log_only' },
  'ERR_RATE_LIMIT_EXCEEDED': { type: 'system', severity: 'non-critical', action: 'log_only' },
  
  // Server errors
  'ERR_SERVER_ERROR': { type: 'system', severity: 'critical' },
  'ERR_INTERNAL_SERVER_ERROR': { type: 'system', severity: 'critical' },
  'ERR_SERVICE_UNAVAILABLE': { type: 'system', severity: 'critical' },
  'ERR_MAINTENANCE_MODE': { type: 'system', severity: 'critical' }
};

export const GlobalErrorProvider = ({ children }) => {
  const [currentError, setCurrentError] = useState(null);

  const triggerError = useCallback((errorDetails) => {
    const { statusCode, errorCode, visibility, originalError } = errorDetails;
    
    console.log('GlobalErrorContext: Processing error:', { statusCode, errorCode, visibility });
    
    // Get error routing configuration
    const routingConfig = ERROR_ROUTING[errorCode];
    
    // Determine error type and severity
    let errorType, severity;
    
    if (routingConfig) {
      errorType = routingConfig.type;
      severity = routingConfig.severity;
    } else {
      // Fallback logic for unknown error codes
      if (visibility === 'user') {
        errorType = 'user';
        severity = 'non-critical';
      } else if (statusCode >= 500) {
        errorType = 'system';
        severity = 'critical';
      } else {
        errorType = 'system';
        severity = 'non-critical';
      }
    }
    
    // Handle system errors
    if (errorType === 'system') {
      console.warn(`System error ${errorCode}:`, originalError);
      
      // For critical system errors, show generic message to user
      if (severity === 'critical') {
        setCurrentError({
          type: 'system',
          severity: 'critical',
          message: 'A system error occurred. Please try again.',
          errorCode,
          canRetry: statusCode >= 500 // Allow retry for server errors
        });
      }
      // Non-critical system errors are just logged, not shown to user
      return;
    }
    
    // Handle user errors - store error code for UI components to localize
    if (errorType === 'user') {
      console.log('GlobalErrorContext: Showing user error:', errorCode);
      
      setCurrentError({
        type: 'user',
        severity,
        errorCode, // Let UI components handle localization
        message: errorCode // Fallback message
      });
    }
  }, []);

  const clearError = useCallback(() => {
    setCurrentError(null);
  }, []);

  return (
    <GlobalErrorContext.Provider
      value={{
        currentError,
        triggerError,
        clearError
      }}
    >
      {children}
    </GlobalErrorContext.Provider>
  );
};

export const useGlobalError = () => useContext(GlobalErrorContext);
